<template>
  <div class="container mx-auto px-4 py-8 max-w-4xl">
    <!-- 로딩 상태 -->
    <div v-if="isLoading" class="animate-pulse">
      <div class="h-8 bg-gray-300 rounded mb-4"></div>
      <div class="h-4 bg-gray-300 rounded mb-2 w-2/3"></div>
      <div class="h-4 bg-gray-300 rounded mb-8 w-1/2"></div>
      <div class="h-64 bg-gray-300 rounded mb-8"></div>
      <div class="h-4 bg-gray-300 rounded mb-2"></div>
      <div class="h-4 bg-gray-300 rounded mb-2 w-3/4"></div>
      <div class="h-4 bg-gray-300 rounded w-1/2"></div>
    </div>

    <!-- 게시글 내용 -->
    <div v-else-if="post" class="space-y-8">
      <!-- 백 버튼 -->
      <div class="flex flex-wrap items-center gap-3">
        <button
          @click="$router.go(-1)"
          class="btn btn-outline btn-sm"
        >
          <ArrowLeftIcon class="w-4 h-4 mr-2" />
          뒤로 가기
        </button>
      </div>

      <!-- 게시글 헤더 -->
      <div class="bg-white rounded-lg border shadow-sm p-6 sm:p-8">
        <div class="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-6">
          <div class="flex-1 min-w-0">
            <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 break-words">
              {{ post.title }}
            </h1>
            <div class="flex flex-wrap items-start text-sm text-gray-600 gap-x-4 gap-y-2">
              <div class="flex items-center min-w-0">
                <UserIcon class="w-4 h-4 mr-1" />
                <span class="min-w-0 break-words">{{ post.user?.name || post.userName }}</span>
              </div>
              <div class="flex items-center min-w-0">
                <ClockIcon class="w-4 h-4 mr-1" />
                <span>{{ formatDate(post.createdAt) }}</span>
              </div>
              <div class="flex items-center min-w-0">
                <MapPinIcon class="w-4 h-4 mr-1" />
                <span class="min-w-0 break-words">{{ post.place }}</span>
              </div>
            </div>
          </div>
          
          <!-- 작성자에게만 보이는 수정/삭제 버튼 -->
          <div v-if="canEditPost" class="flex flex-wrap gap-2">
            <RouterLink
              :to="`/posts/${post.id}/edit`"
              class="btn btn-outline btn-sm"
            >
              <PencilIcon class="w-4 h-4 mr-1" />
              수정
            </RouterLink>
            <button
              @click="showDeleteModal = true"
              class="btn bg-red-600 hover:bg-red-700 text-white btn-sm"
            >
              <TrashIcon class="w-4 h-4 mr-1" />
              삭제
            </button>
          </div>
        </div>

        <!-- 게시글 정보 -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 p-4 sm:p-6 bg-gray-50 rounded-lg">
          <div class="space-y-3">
            <div class="flex items-start gap-3">
              <HomeIcon class="w-5 h-5 text-gray-400 mr-3" />
              <div class="min-w-0">
                <div class="text-sm font-medium text-gray-900">장소</div>
                <div class="text-sm text-gray-600 break-words">{{ post.place }}</div>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <MapPinIcon class="w-5 h-5 text-gray-400 mr-3" />
              <div class="min-w-0">
                <div class="text-sm font-medium text-gray-900">주소</div>
                <div class="text-sm text-gray-600 break-words">{{ post.address }}</div>
              </div>
            </div>
          </div>
          <div class="space-y-3">
            <div class="flex items-start gap-3">
              <CalendarDaysIcon class="w-5 h-5 text-gray-400 mr-3" />
              <div class="min-w-0">
                <div class="text-sm font-medium text-gray-900">예약 가능 기간</div>
                <div class="text-sm text-gray-600 break-words">
                  {{ formatDate(post.startDate) }} - {{ formatDate(post.endDate) }}
                </div>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <UserGroupIcon class="w-5 h-5 text-gray-400 mr-3" />
              <div class="min-w-0">
                <div class="text-sm font-medium text-gray-900">좌석 현황</div>
                <div class="text-sm text-gray-600 break-words">
                  {{ reservedSeatCount }} / {{ totalSeatCount }} 예약
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="gap-6 mb-8 p-4 rounded-lg">
          <KakaoMap
              :placeName="post.place"
              :address="post.address"
              :isDetailPage="true"
              @select="selectPlace"
          />
        </div>

        <!-- 게시글 내용 -->
        <div class="prose max-w-none mb-8 p-1">
          <div class="whitespace-pre-wrap text-gray-800 leading-relaxed">
            {{ post.content }}
          </div>
        </div>

        <!-- 예약 버튼 -->
        <div v-if="canMakeReservation" class="border-t pt-6">
          <div class="flex justify-center">
            <button
              @click="showReservationModal = true"
              :disabled="!hasAvailableSeats"
              class="btn btn-primary btn-md sm:btn-lg px-6 sm:px-8"
              :class="{ 'opacity-50 cursor-not-allowed': !hasAvailableSeats }"
            >
              {{ hasAvailableSeats ? '좌석 예약하기' : '예약 가능한 좌석이 없습니다' }}
            </button>
          </div>
        </div>
        
        <!-- 이미 예약한 경우 -->
        <div v-else-if="hasReachedReservationLimit" class="border-t pt-6">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <CheckCircleIcon class="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p class="text-blue-800 font-medium">예약 가능 좌석 수를 모두 사용했습니다</p>
            <RouterLink to="/my/reservations" class="btn btn-outline btn-sm mt-3">
              내 예약 보기
            </RouterLink>
          </div>
        </div>
      </div>

      <!-- 좌석 배치도 -->
      <div v-if="seatLayoutObj" class="bg-white rounded-lg border shadow-sm p-6 sm:p-8">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">좌석 배치도</h2>
        <SeatLayout
          :key="seatLayoutKey"
          :mode="'Detail'"
          :layout="seatLayoutObj"
          :reserved-seat-numbers="reservedSeatNumbers"
          :my-seat-numbers="mySeatNumbers"
          :readonly="true"
        />
      </div>
    </div>

    <!-- 에러 상태 -->
    <div v-else class="text-center py-16">
      <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <ExclamationTriangleIcon class="w-8 h-8 text-gray-400" />
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">게시글을 찾을 수 없습니다</h3>
      <p class="text-gray-600 mb-6">삭제되었거나 존재하지 않는 게시글입니다.</p>
      <RouterLink to="/posts" class="btn btn-primary btn-sm">
        목록으로 돌아가기
      </RouterLink>
    </div>

    <!-- 좌석 예약 모달 -->
    <SeatReservationModal
      v-if="showReservationModal"
      :post="post as Post"
      :layout="seatLayoutObj"
      :reserved-seat-numbers="reservedSeatNumbers"
      :max-seats-per-user="Math.max(0, (post?.maxSeatsPerUser || 1) - mySeatNumbers.length)"
      :readonly="true"
      @close="showReservationModal = false"
      @reserved="handleReservationSuccess"
    />

    <!-- 삭제 확인 모달 -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-red-900">게시글 삭제 확인</h3>
          <button @click="showDeleteModal = false" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        
        <div class="space-y-4">
          <div class="bg-red-50 p-4 rounded-md">
            <ExclamationTriangleIcon class="w-5 h-5 text-red-400 inline mr-2" />
            <span class="text-red-800 font-medium">이 작업은 되돌릴 수 없습니다!</span>
          </div>
          
          <p class="text-gray-700">
            '{{ post?.title }}' 게시글을 삭제하시겠습니까? 모든 댓글과 예약 정보도 함께 삭제됩니다.
          </p>
          
          <div class="flex gap-3 pt-4">
            <button
              @click="showDeleteModal = false"
              class="btn btn-outline btn-sm flex-1"
            >
              취소
            </button>
            <button
              @click="deletePost"
              :disabled="isDeleting"
              class="btn btn-sm bg-red-600 hover:bg-red-700 text-white flex-1"
              :class="{ 'opacity-50 cursor-not-allowed': isDeleting }"
            >
              <span v-if="isDeleting">삭제 중...</span>
              <span v-else>삭제하기</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, onMounted, reactive} from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
  ArrowLeftIcon,
  UserIcon,
  ClockIcon,
  MapPinIcon,
  HomeIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import { postApi, reservationApi } from '@/services/api'
import type { Post, SeatLayoutInfo } from '@/types'
import { useAuthStore } from '@/stores/auth'
import { useBreadcrumbStore } from '@/stores/breadcrumbs'
import { useToast } from 'vue-toastification'
import SeatReservationModal from '@/components/SeatReservationModal.vue'
import {deserializeSeatStatus} from "@/utils";
import SeatLayout from '@/components/SeatLayout.vue'
import KakaoMap from "@/components/KakaoMap.vue";
import { format } from 'date-fns';

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const breadcrumbStore = useBreadcrumbStore()
const toast = useToast()

const isAuthenticated = computed(() => authStore.isAuthenticated)
const user = computed(() => authStore.user)

const post = ref<Post | null>(null)
const reservedSeatNumbers = ref<number[]>([])
const mySeatNumbers = ref<number[]>([])
const seatStatusLoaded = ref(false)
const seatLayoutObj = reactive<SeatLayoutInfo>({
  rows: 0,
  seatsPerRow: 0,
  totalSeats: 0,
  seatStatus: [[]]
})
const seatLayoutKey = ref(0)

const isLoading = ref(true)
const isDeleting = ref(false)
const showDeleteModal = ref(false)
const showReservationModal = ref(false)
const hasReachedReservationLimit = ref(false)

const postId = computed(() => parseInt(route.params.id as string))

// 게시글 수정 가능 여부
const canEditPost = computed(() => {
  if (!isAuthenticated.value || !user.value || !post.value) return false
  return user.value.role === 'ADMIN' || user.value.id === post.value.user?.id
})

// 예약 가능 여부
const canMakeReservation = computed(() => {
  if (!isAuthenticated.value || !post.value) return false
  const maxSeatsPerUser = post.value.maxSeatsPerUser || 1
  const remainingSeats = maxSeatsPerUser - mySeatNumbers.value.length
  const now = new Date()
  const startDate = new Date(post.value.startDate)
  const endDate = new Date(post.value.endDate)
  return remainingSeats > 0 && now >= startDate && now <= endDate
})

const totalSeatCount = computed(() => {
  if (post.value?.totalSeats && post.value.totalSeats > 0) {
    return post.value.totalSeats
  }
  return seatLayoutObj.totalSeats || 0
})

const reservedSeatCount = computed(() => {
  if (seatStatusLoaded.value) {
    return reservedSeatNumbers.value.length
  }
  return post.value?.reservedSeats || 0
})

// 예약 가능한 좌석 있는지 확인
const hasAvailableSeats = computed(() => {
  if (!post.value) return false
  return totalSeatCount.value > reservedSeatCount.value
})

// 날짜 포맷
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return format(date, 'yy.MM.dd. HH:mm')
}

// 게시글 로드
const loadPost = async () => {
  try {
    isLoading.value = true
    const response = await postApi.getPost(postId.value)
    post.value = response.data
    breadcrumbStore.setLabel('PostDetail', post.value.title)

    const seatStatus: number[][] = deserializeSeatStatus(post.value.seatLayout)
    const rows: number = seatStatus.length
    const seatsPerRow: number = seatStatus[0].length
    let totalSeats: number = 0;
    seatStatus.forEach(x => x.forEach(y => { if (y == 1) { totalSeats++ } }))
    seatLayoutObj.rows = rows
    seatLayoutObj.seatsPerRow = seatsPerRow
    seatLayoutObj.totalSeats = totalSeats
    seatLayoutObj.seatStatus = seatStatus
    // 예약 정보도 로드
    await Promise.all([
      loadSeatStatus(),
      loadMySeatNumbers()
    ])
    // 컴포넌트 변경 적용하기 위한 키
    seatLayoutKey.value++
  } catch (error: any) {
    console.error('Failed to load post:', error)
    if (error.response?.status === 404) {
      post.value = null
    } else {
      toast.error('게시글을 불러오는데 실패했습니다.')
    }
  } finally {
    isLoading.value = false
  }
}

// 예약 좌석 현황 로드
const loadSeatStatus = async () => {
  try {
    const response = await reservationApi.getPostSeatStatus(postId.value)
    reservedSeatNumbers.value = response.data.reservedSeatNumbers || []
    seatStatusLoaded.value = true
  } catch (error) {
    console.error('Failed to load seat status:', error)
    seatStatusLoaded.value = false
    reservedSeatNumbers.value = []
  }
}

// 내 예약 좌석 로드
const loadMySeatNumbers = async () => {
  if (!isAuthenticated.value) {
    mySeatNumbers.value = []
    hasReachedReservationLimit.value = false
    return
  }

  try {
    const response = await reservationApi.getMySeatNumbers(postId.value)
    mySeatNumbers.value = response.data || []
    const maxSeatsPerUser = post.value?.maxSeatsPerUser || 1
    hasReachedReservationLimit.value = mySeatNumbers.value.length >= maxSeatsPerUser
  } catch (error) {
    console.error('Failed to load my seats:', error)
  }
}

// 게시글 삭제
const deletePost = async () => {
  if (!post.value) return
  
  try {
    isDeleting.value = true
    await postApi.deletePost(post.value.id)
    toast.success('게시글이 삭제되었습니다.')
    router.push('/posts')
  } catch (error) {
    console.error('Failed to delete post:', error)
    toast.error('게시글 삭제에 실패했습니다.')
  } finally {
    isDeleting.value = false
    showDeleteModal.value = false
  }
}

// 예약 성공 처리
const handleReservationSuccess = () => {
  showReservationModal.value = false
  // 예약 정보 재로드
  loadSeatStatus()
  loadMySeatNumbers()
  loadPost() // 예약 수 업데이트
}

// 카카오맵 위치 선택 이벤트
const selectPlace = (place: any) => {
  place.place_name
  place.road_address_name || place.address_name
}

onMounted(() => {
  loadPost()
})
</script>
